import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import './TourVehicule.css';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function TourVehicule({ user, editMode, setHeaderAction }) {
  const [tab, setTab] = useState('liste'); // 'liste' | 'archives'
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Concession : celle du menu (2026-10-26). Les dossiers portent le NOM de la
  // concession dans concession_code (ex. « BLUE AUTOMOBILES »), d'où concession_nom.
  const [concessionFilter] = useState(() => (user?.concession_id && user?.concession_nom) || '');
  const [dateFilter, setDateFilter] = useState(''); // 'today' | '7d' | '30d' | ''
  const [detail, setDetail] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const isSuperAdmin = user?.role === 'super-admin';

  // Pas de bouton d'action en header pour cette page (consultation uniquement)
  useEffect(() => {
    if (setHeaderAction) setHeaderAction(null);
  }, [setHeaderAction]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { archived: tab === 'archives' ? 'true' : 'false' };
      if (search.trim()) params.search = search.trim();
      if (concessionFilter) params.concession = concessionFilter;
      if (dateFilter === 'today') {
        const today = new Date().toISOString().slice(0, 10);
        params.date_from = today;
        params.date_to = today;
      } else if (dateFilter === '7d') {
        const d = new Date(); d.setDate(d.getDate() - 7);
        params.date_from = d.toISOString().slice(0, 10);
      } else if (dateFilter === '30d') {
        const d = new Date(); d.setDate(d.getDate() - 30);
        params.date_from = d.toISOString().slice(0, 10);
      }
      const { data } = await api.get('/tour-vehicule', { params });
      setDossiers(Array.isArray(data.dossiers) ? data.dossiers : []);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('[tour-vehicule] load', err);
      setDossiers([]);
    } finally {
      setLoading(false);
    }
  }, [tab, search, concessionFilter, dateFilter]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === dossiers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(dossiers.map(d => d.id)));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selectedIds.size} dossier(s) ?`)) return;
    try {
      for (const id of selectedIds) {
        await api.delete(`/tour-vehicule/${id}`);
      }
      await load();
    } catch (err) {
      console.error('[tour-vehicule] delete', err);
      alert('Erreur lors de la suppression');
    }
  };

  const archiveDossier = async (id, archived) => {
    try {
      await api.patch(`/tour-vehicule/${id}`, { archived });
      await load();
    } catch (err) {
      console.error('[tour-vehicule] archive', err);
    }
  };

  const openDetail = async (dossier) => {
    setDetail({ dossier, photos: [], loading: true });
    try {
      const { data } = await api.get(`/tour-vehicule/${dossier.id}`);
      // On marque toutes les photos comme "loading" pour afficher le spinner,
      // puis on déclenche les téléchargements en parallèle.
      setDetail({
        dossier: data.dossier,
        photos: data.photos.map(p => ({ ...p, image_data: null, loading: true })),
        loading: false,
      });
      data.photos.forEach(p => loadPhoto(data.dossier.id, p.id));
    } catch (err) {
      console.error('[tour-vehicule] detail', err);
      setDetail(null);
    }
  };

  const loadPhoto = async (dossierId, photoId) => {
    setDetail(d => ({
      ...d,
      photos: d.photos.map(p => p.id === photoId ? { ...p, loading: true } : p),
    }));
    try {
      const { data } = await api.get(`/tour-vehicule/${dossierId}/photo/${photoId}`);
      setDetail(d => ({
        ...d,
        photos: d.photos.map(p => p.id === photoId
          ? { ...p, image_data: data.image_data, loading: false }
          : p
        ),
      }));
    } catch (err) {
      console.error('[tour-vehicule] photo', err);
      setDetail(d => ({
        ...d,
        photos: d.photos.map(p => p.id === photoId ? { ...p, loading: false } : p),
      }));
    }
  };

  const downloadPdf = async () => {
    if (!detail?.dossier?.id) return;
    // Onglet ouvert pendant le clic : le serveur peut mettre quelques secondes à
    // regénérer un ancien PDF, et un window.open tardif serait bloqué.
    const onglet = window.open('', '_blank');
    if (onglet) {
      onglet.opener = null;
      onglet.document.title = 'PDF récap…';
      onglet.document.body.innerHTML = '<p style="font-family:sans-serif;padding:24px">Préparation du PDF…</p>';
    }
    try {
      const { data } = await api.get(`/tour-vehicule/${detail.dossier.id}/pdf`);
      if (!data.url) throw new Error('URL manquante');
      if (onglet) onglet.location.href = data.url;
      else window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[tour-vehicule] pdf', err);
      if (onglet) onglet.close();
      alert('PDF indisponible');
    }
  };

  const showDelete = editMode && isSuperAdmin;

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button
          className={tab === 'liste' ? 'active' : ''}
          onClick={() => setTab('liste')}
        >📋 Dossiers actifs</button>
        <button
          className={tab === 'archives' ? 'active' : ''}
          onClick={() => setTab('archives')}
        >🗄️ Archives</button>
      </div>

      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        <select
          className={`page-select ${dateFilter ? 'active-filter' : ''}`}
          value={dateFilter}
          onChange={e => setDateFilter(e.target.value)}
        >
          <option value="">Toutes dates</option>
          <option value="today">Aujourd'hui</option>
          <option value="7d">7 derniers jours</option>
          <option value="30d">30 derniers jours</option>
        </select>
        {showDelete && selectedIds.size > 0 && (
          <button
            className="tv-delete-bulk"
            onClick={deleteSelected}
          >🗑️ Supprimer la sélection ({selectedIds.size})</button>
        )}
      </div>

      <div className="table-container">
        <table className="tv-table">
          <thead>
            <tr>
              {showDelete && (
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={dossiers.length > 0 && selectedIds.size === dossiers.length}
                    onChange={toggleSelectAll}
                  />
                </th>
              )}
              <th>Date</th>
              <th>OR</th>
              <th>Immat</th>
              <th>Client</th>
              <th>Véhicule</th>
              <th>Nb photos</th>
              <th>Géoloc</th>
              <th>Concession</th>
              <th>Réceptionnaire</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={showDelete ? 11 : 10} className="tv-empty">Chargement…</td></tr>
            )}
            {!loading && dossiers.length === 0 && (
              <tr><td colSpan={showDelete ? 11 : 10} className="tv-empty">Aucun dossier.</td></tr>
            )}
            {!loading && dossiers.map(d => (
              <tr key={d.id} onClick={() => openDetail(d)} className="tv-row">
                {showDelete && (
                  <td onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(d.id)}
                      onChange={() => toggleSelect(d.id)}
                    />
                  </td>
                )}
                <td>{formatDate(d.created_at)}</td>
                <td><strong>{d.numero_or}</strong></td>
                <td><span className="tv-immat">{d.immat || '—'}</span></td>
                <td>{d.nom_client || <em className="tv-muted">à enrichir</em>}</td>
                <td>{[d.marque, d.modele].filter(Boolean).join(' ') || '—'}</td>
                <td className="tv-num">{d.nb_photos}</td>
                <td>
                  <span className={`tv-badge ${d.geoloc_disponible ? 'tv-badge-yes' : 'tv-badge-no'}`}>
                    {d.geoloc_disponible ? 'Oui' : 'Non'}
                  </span>
                </td>
                <td>{d.concession_code || '—'}</td>
                <td>{d.agent || '—'}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="action-buttons">
                    <button className="btn-icon" title="Voir" onClick={() => openDetail(d)}>👁️</button>
                    {tab === 'liste' && (
                      <button className="btn-icon" title="Archiver" onClick={() => archiveDossier(d.id, true)}>✅</button>
                    )}
                    {tab === 'archives' && (
                      <button className="btn-icon" title="Désarchiver" onClick={() => archiveDossier(d.id, false)}>↩️</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <DetailModal
          detail={detail}
          onClose={() => setDetail(null)}
          onLoadPhoto={loadPhoto}
          onDownloadPdf={downloadPdf}
        />
      )}
    </div>
  );
}

function DetailModal({ detail, onClose, onLoadPhoto, onDownloadPdf }) {
  const { dossier, photos } = detail;
  const [lightbox, setLightbox] = useState(null);

  const mapsUrl = (lat, lon) => `https://www.google.com/maps?q=${lat},${lon}`;

  return (
    <div className="modal-overlay">
      <div className="modal-content large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🚗 Tour du véhicule — OR {dossier.numero_or}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="tv-detail-grid">
            <div className="tv-detail-meta">
              <Row label="Date" value={formatDate(dossier.created_at)} />
              <Row label="Réceptionnaire" value={dossier.agent} />
              <Row label="Concession" value={dossier.concession_code} />
              <Row label="Immatriculation" value={dossier.immat} mono />
              <Row label="Marque / Modèle" value={[dossier.marque, dossier.modele].filter(Boolean).join(' ')} />
              <Row label="Client" value={dossier.nom_client} />
              <Row label="Photos" value={photos.length} />
              <Row label="Statut" value={dossier.statut} />
            </div>

            <div className="tv-detail-actions">
              <button className="btn-primary" onClick={onDownloadPdf}>
                📄 Télécharger le PDF récap
              </button>
            </div>
          </div>

          <div className="tv-detail-section">
            <h3>📍 Géolocalisation</h3>
            {dossier.geoloc_disponible ? (
              <>
                <div className="tv-geoloc-info">
                  <span className="tv-geoloc-coords">
                    {Number(dossier.latitude).toFixed(6)}, {Number(dossier.longitude).toFixed(6)}
                  </span>
                  {dossier.accuracy != null && (
                    <span className="tv-geoloc-acc"> (±{Math.round(Number(dossier.accuracy))} m)</span>
                  )}
                </div>
                <iframe
                  title="Localisation véhicule"
                  className="tv-map-iframe"
                  src={`https://maps.google.com/maps?q=${dossier.latitude},${dossier.longitude}&z=19&t=k&output=embed`}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                <a
                  className="tv-map-link"
                  href={mapsUrl(dossier.latitude, dossier.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ouvrir dans Google Maps ↗
                </a>
              </>
            ) : (
              <p className="tv-muted">Géolocalisation non disponible (refusée ou indisponible au moment de la prise de photo).</p>
            )}
          </div>

          <div className="tv-detail-section">
            <h3>📷 Photos ({photos.length})</h3>
            <div className="tv-photos-grid">
              {photos.map(p => (
                <div key={p.id} className="tv-photo-cell" onClick={() => p.image_data && setLightbox(p)}>
                  {p.image_data ? (
                    <img src={`data:image/jpeg;base64,${p.image_data}`} alt={`Photo ${p.id}`} />
                  ) : p.loading ? (
                    <div className="tv-photo-loading">⏳</div>
                  ) : (
                    <button className="tv-photo-load" onClick={() => onLoadPhoto(dossier.id, p.id)}>
                      Charger
                    </button>
                  )}
                </div>
              ))}
              {photos.length === 0 && <p className="tv-muted">Aucune photo enregistrée.</p>}
            </div>
          </div>

          <div className="tv-detail-section">
            <h3>✍️ Signature client</h3>
            {dossier.signature_data ? (
              <img
                className="tv-signature-img"
                src={`data:image/png;base64,${dossier.signature_data}`}
                alt="Signature client"
              />
            ) : (
              <p className="tv-muted">Pas de signature enregistrée.</p>
            )}
          </div>
        </div>
      </div>

      {lightbox && (
        <div className="tv-lightbox" onClick={() => setLightbox(null)}>
          <img src={`data:image/jpeg;base64,${lightbox.image_data}`} alt="Photo agrandie" />
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="tv-row-meta">
      <span className="tv-row-label">{label}</span>
      <span className={`tv-row-value ${mono ? 'tv-mono' : ''}`}>{value || '—'}</span>
    </div>
  );
}

export default TourVehicule;
